import type { ReactNode } from 'react'
import { useState } from 'react'
import { Badge, Button, DashboardPanel } from '@rentflowdev/ui'
import { useTranslation } from 'react-i18next'
import { CheckIcon, CopyIcon } from 'lucide-react'

interface IntegrationCardProps {
	logo:        ReactNode
	name:        string
	host?:       string
	hostHref?:   string
	description: string
	connected?:  boolean
	connectedAs?: string
	itemsCount?: number
	primaryLabel: string
	secondaryLabel?: string
	onPrimary:   () => void
	onSecondary?: () => void
	disabled?:   boolean
	/** Extra action(s) rendered next to the primary/secondary buttons. */
	extraAction?: ReactNode
	/** Feed URL to display as a copyable field (shown only when connected). */
	feedUrl?: string
}

export function IntegrationCard({
	logo, name, host, hostHref, description,
	connected = false,
	connectedAs, itemsCount,
	primaryLabel, secondaryLabel,
	onPrimary, onSecondary,
	disabled = false,
	extraAction,
	feedUrl,
}: IntegrationCardProps) {
	const { t } = useTranslation()
	const [copied, setCopied] = useState(false)

	function handleCopy() {
		if (!feedUrl) return
		void navigator.clipboard.writeText(feedUrl)
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
	}

	const subtitle = host
		? hostHref
			? (
				<a
					className="text-xs leading-5 text-(--color-muted-foreground) hover:text-(--color-foreground) hover:underline"
					href={hostHref}
					target="_blank"
					rel="noreferrer"
				>
					{host} ↗
				</a>
			)
			: <span className="text-xs leading-5 text-(--color-muted-foreground)">{host}</span>
		: undefined

	const actions = (
		<div className="flex items-center gap-2">
			{connected && (
				<Badge variant="success" size="sm">
					{t('settings.integrations.status.connected')}
				</Badge>
			)}
			<div className="size-10 shrink-0">{logo}</div>
		</div>
	)

	return (
		<DashboardPanel title={name} subtitle={subtitle} actions={actions}>
			{connected && (connectedAs || itemsCount !== undefined) && (
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-(--color-muted-foreground)">
					{connectedAs && <span>{t('settings.integrations.status.connectedAs', { name: connectedAs })}</span>}
					{itemsCount !== undefined && (
						<span>{t('settings.integrations.status.itemsCount', { count: itemsCount })}</span>
					)}
				</div>
			)}
			<p className="text-sm leading-5 text-(--color-muted-foreground)">{description}</p>
			{feedUrl && (
				<div className="flex items-center gap-2 rounded-(--radius-md) border border-(--color-border) bg-(--color-muted) px-3 py-2">
					<span className="min-w-0 flex-1 truncate font-mono text-xs text-(--color-muted-foreground)">{feedUrl}</span>
					<button
						type="button"
						onClick={handleCopy}
						className="shrink-0 text-(--color-muted-foreground) hover:text-(--color-foreground)"
						aria-label={t('common.copy')}
					>
						{copied ? <CheckIcon className="size-3.5 text-(--color-success)" /> : <CopyIcon className="size-3.5" />}
					</button>
				</div>
			)}
			<div className="flex flex-wrap gap-2">
				<Button
					size="sm"
					variant={connected ? 'secondary' : 'default'}
					onClick={onPrimary}
					disabled={disabled}
				>
					{primaryLabel}
				</Button>
				{secondaryLabel && onSecondary && (
					<Button size="sm" variant="ghost" onClick={onSecondary} disabled={disabled}>
						{secondaryLabel}
					</Button>
				)}
				{extraAction}
			</div>
		</DashboardPanel>
	)
}
